/**
 * Storage Manager - Firebase Storage 관리
 */

import { Trans, t } from '@lingui/macro';
import {
    HardDrive, FolderOpen, File, Loader2, AlertTriangle,
    RefreshCw, Trash2, CheckCircle, XCircle
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { getAdminFunc } from '../../services/SuperAdminFirebase';

interface FolderStats {
    path: string;
    bytes: number;
    fileCount: number;
}

interface StorageStats {
    totalBytes: number;
    fileCount: number;
    folders: FolderStats[];
}

interface OrphanFile {
    path: string;
    sizeBytes: number;
    createdAt: string | null;
    reason: string;
}

interface OrphanScanResult {
    success: boolean;
    dryRun: boolean;
    orphanFiles: OrphanFile[];
    totalFound: number;
    totalBytes: number;
    deletedCount: number;
}

interface WorkspaceStorageInfo {
    workspaceId: string;
    workspaceName: string;
    calculatedBytes: number;
    storedBytes: number;
    fileCount: number;
    discrepancy: number;
}

const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DevAdminStorage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState<StorageStats | null>(null);
    const [orphanResult, setOrphanResult] = useState<OrphanScanResult | null>(null);
    const [recalcResult, setRecalcResult] = useState<WorkspaceStorageInfo[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [activeAction, setActiveAction] = useState<string | null>(null);

    // Allocation Test State
    const [allocWsId, setAllocWsId] = useState('');
    const [allocUserId, setAllocUserId] = useState('');
    const [allocMB, setAllocMB] = useState<string>('0');

    useEffect(() => {
        loadStorageStats();
    }, []);

    const loadStorageStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const getStorageStats = getAdminFunc('getStorageStats');
            const result = await getStorageStats({});
            const data = result.data as { success: boolean; stats: StorageStats };
            if (data.success) {
                setStats(data.stats);
            }
        } catch (err: any) {
            console.error('Failed to load storage stats:', err);
            setError(err.message || 'Failed to load storage stats');
        } finally {
            setLoading(false);
        }
    };

    const handleRecalculateStorage = async () => {
        setActiveAction('recalculate');
        setError(null);
        try {
            const recalculateWorkspaceStorage = getAdminFunc('recalculateWorkspaceStorage');
            const result = await recalculateWorkspaceStorage({ updateStats: false });
            const data = result.data as { success: boolean; workspaces: WorkspaceStorageInfo[] };
            if (data.success) {
                setRecalcResult(data.workspaces);
            }
        } catch (err: any) {
            console.error('Failed to recalculate storage:', err);
            setError(err.message || 'Failed to recalculate storage');
        } finally {
            setActiveAction(null);
        }
    };

    const handleFindOrphanFiles = async (dryRun: boolean = true) => {
        setActiveAction('orphan');
        setError(null);
        try {
            const findOrphanFiles = getAdminFunc('findOrphanFiles');
            const result = await findOrphanFiles({ dryRun, limit: 100 });
            const data = result.data as OrphanScanResult;
            if (data.success) {
                setOrphanResult(data);
            }
        } catch (err: any) {
            console.error('Failed to find orphan files:', err);
            setError(err.message || 'Failed to find orphan files');
        } finally {
            setActiveAction(null);
        }
    };

    const handleUpdateAllocation = async () => {
        if (!allocWsId || !allocUserId) {
            setError('Workspace ID and User ID are required');
            return;
        }

        setActiveAction('updateAllocation');
        setError(null);
        try {
            const updateAllocation = getAdminFunc('updateAllocation');
            await updateAllocation({
                workspaceId: allocWsId,
                targetUserId: allocUserId,
                newBytes: parseFloat(allocMB) * 1024 * 1024
            });
            alert('Allocation Updated Successfully!');
        } catch (err: any) {
            console.error('Failed to update allocation:', err);
            setError(err.message || 'Failed to update allocation');
        } finally {
            setActiveAction(null);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">
                            <Trans>Storage Manager</Trans>
                        </h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            <Trans>Firebase Storage usage and file management</Trans>
                        </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={loadStorageStats} disabled={loading}>
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4" />
                        )}
                        <span className="ml-2"><Trans>Refresh</Trans></span>
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                <div className="max-w-4xl space-y-6">
                    {/* Error Alert */}
                    {error && (
                        <Card className="border-red-200 bg-red-50">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 text-red-700">
                                    <XCircle className="w-5 h-5" />
                                    <span>{error}</span>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Storage Overview */}
                    {stats && (
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800 mb-4">
                                <Trans>Storage Overview</Trans>
                            </h2>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="text-sm text-slate-500 mb-1">Total Size</div>
                                        <div className="text-2xl font-bold text-slate-900">
                                            {formatBytes(stats.totalBytes)}
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="text-sm text-slate-500 mb-1">Total Files</div>
                                        <div className="text-2xl font-bold text-slate-900">
                                            {stats.fileCount.toLocaleString()}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            <div className="grid gap-3">
                                {stats.folders.map((folder) => (
                                    <Card key={folder.path}>
                                        <CardContent className="p-4 flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                                <FolderOpen className="w-5 h-5 text-slate-500" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-mono text-sm text-slate-700">
                                                    {folder.path}/
                                                </div>
                                                <div className="text-xs text-slate-500">
                                                    {folder.fileCount} files
                                                </div>
                                            </div>
                                            <Badge variant="secondary">
                                                {formatBytes(folder.bytes)}
                                            </Badge>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div>
                        <h2 className="text-lg font-semibold text-slate-800 mb-4">
                            <Trans>Actions</Trans>
                        </h2>
                        <div className="grid gap-3">
                            {/* Recalculate Storage */}
                            <Card>
                                <CardContent className="p-4 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                        <HardDrive className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-medium text-slate-900">
                                            전체 용량 재계산
                                        </div>
                                        <div className="text-sm text-slate-500">
                                            모든 워크스페이스의 스토리지 용량을 다시 계산합니다.
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={handleRecalculateStorage}
                                        disabled={activeAction === 'recalculate'}
                                    >
                                        {activeAction === 'recalculate' ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Trans>Run</Trans>
                                        )}
                                    </Button>
                                </CardContent>
                            </Card>

                            {/* Find Orphan Files */}
                            <Card>
                                <CardContent className="p-4 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                        <Trash2 className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-medium text-slate-900">
                                            고아 파일 탐지
                                        </div>
                                        <div className="text-sm text-slate-500">
                                            삭제된 Room에 남아있는 파일을 찾습니다.
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={() => handleFindOrphanFiles(true)}
                                        disabled={activeAction === 'orphan'}
                                    >
                                        {activeAction === 'orphan' ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Trans>Scan</Trans>
                                        )}
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Allocation Testing Tool */}
                    <div>
                        <h2 className="text-lg font-semibold text-slate-800 mb-4">
                            Test Allocation Tool
                        </h2>
                        <Card>
                            <CardContent className="p-4 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Workspace ID</Label>
                                        <Input
                                            value={allocWsId}
                                            onChange={(e) => setAllocWsId(e.target.value)}
                                            placeholder="ws_..."
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Target User UID</Label>
                                        <Input
                                            value={allocUserId}
                                            onChange={(e) => setAllocUserId(e.target.value)}
                                            placeholder="uid_..."
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Allocation (MB)</Label>
                                        <Input
                                            type="number"
                                            value={allocMB}
                                            onChange={(e) => setAllocMB(e.target.value)}
                                            placeholder="1024"
                                        />
                                    </div>
                                </div>
                                <Button
                                    onClick={handleUpdateAllocation}
                                    disabled={activeAction === 'updateAllocation'}
                                >
                                    {activeAction === 'updateAllocation' ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <HardDrive className="w-4 h-4 mr-2" />
                                    )}
                                    Update Allocation
                                </Button>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Recalculation Results */}
                    {recalcResult && (
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-green-500" />
                                용량 재계산 결과
                            </h2>
                            <div className="grid gap-2">
                                {recalcResult.map((ws) => (
                                    <Card key={ws.workspaceId}>
                                        <CardContent className="p-3">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="font-medium text-slate-900">{ws.workspaceName}</div>
                                                    <div className="text-xs text-slate-500 font-mono">{ws.workspaceId}</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-sm">
                                                        <span className="text-slate-500">계산:</span>{' '}
                                                        <span className="font-medium">{formatBytes(ws.calculatedBytes)}</span>
                                                    </div>
                                                    {ws.discrepancy !== 0 && (
                                                        <div className={`text-xs ${ws.discrepancy > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                                            차이: {ws.discrepancy > 0 ? '+' : ''}{formatBytes(ws.discrepancy)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Orphan Files Results */}
                    {orphanResult && (
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                {orphanResult.totalFound === 0 ? (
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                ) : (
                                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                                )}
                                고아 파일 스캔 결과
                            </h2>
                            <Card className="mb-4">
                                <CardContent className="p-4">
                                    <div className="grid grid-cols-3 gap-4 text-center">
                                        <div>
                                            <div className="text-2xl font-bold text-slate-900">
                                                {orphanResult.totalFound}
                                            </div>
                                            <div className="text-sm text-slate-500">발견된 파일</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-slate-900">
                                                {formatBytes(orphanResult.totalBytes)}
                                            </div>
                                            <div className="text-sm text-slate-500">총 용량</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-slate-900">
                                                {orphanResult.deletedCount}
                                            </div>
                                            <div className="text-sm text-slate-500">삭제됨</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {orphanResult.orphanFiles.length > 0 && (
                                <>
                                    <div className="text-sm text-slate-600 mb-2">
                                        발견된 파일 목록 (최대 100개):
                                    </div>
                                    <div className="max-h-64 overflow-auto border rounded-lg bg-white">
                                        {orphanResult.orphanFiles.map((file, idx) => (
                                            <div
                                                key={idx}
                                                className="px-3 py-2 border-b last:border-b-0 flex items-center gap-2"
                                            >
                                                <File className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs font-mono text-slate-700 truncate">
                                                        {file.path}
                                                    </div>
                                                </div>
                                                <Badge variant="secondary" className="text-xs">
                                                    {formatBytes(file.sizeBytes)}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>

                                    {orphanResult.dryRun && orphanResult.totalFound > 0 && (
                                        <div className="mt-4 flex justify-end">
                                            <Button
                                                variant="destructive"
                                                onClick={() => handleFindOrphanFiles(false)}
                                                disabled={activeAction === 'orphan'}
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                삭제 실행
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DevAdminStorage;
